import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { DialogCloseAction, GalaxyToastService, MenuItem } from '@ebcont/ebcont.galaxy.js';
import { TranslateService } from '@ngx-translate/core';
import { firstValueFrom } from 'rxjs';
import { HttpClient } from '@angular/common/http';
import { environment } from '@env/environment';
import { ProfileService } from '@core/services/profile/profile.service';

@Component({
  selector: 'app-hoc-profile-download-form-component',
  templateUrl: './hoc-profile-download-form.component.html',
  styleUrls: ['./hoc-profile-download-form.component.css'],
  standalone: false
})
export class HocProfileDownloadFormComponent implements OnInit {
  downloadForm!: FormGroup;

  @Input() email?: string;

  @Output() closeDialog = new EventEmitter<DialogCloseAction>();

  public formatItems: MenuItem[] = [
    { label: 'PDF', isRouterLink: false, id: 'pdf' },
    { label: 'DOCX', isRouterLink: false, id: 'docx' }
  ];

  public languageItems: MenuItem[] = [
    { label: 'language.de', isRouterLink: false, id: 'de' },
    { label: 'language.en', isRouterLink: false, id: 'en' }
  ];

  public translationItems: MenuItem[] = [
    { label: 'team.download_profile.original', isRouterLink: false, id: 'true' },
    { label: 'team.download_profile.translation', isRouterLink: false, id: 'false' }
  ];

  constructor(
    private fb: FormBuilder,
    private toastService: GalaxyToastService,
    private translateService: TranslateService,
    private http: HttpClient,
    private profileService: ProfileService
  ) {}

  ngOnInit() {
    this.downloadForm = this.fb.group({
      format: this.fb.control([this.formatItems[0]], [Validators.required]),
      language: this.fb.control([this.languageItems[0]], [Validators.required]),
      original: this.fb.control([this.translationItems[0]], [Validators.required]),
      short: this.fb.control(false),
      anonymous: this.fb.control(false)
    });
  }

  private getDownloadParams() {
    return {
      format: this.downloadForm.get('format')?.value[0].id,
      language: this.downloadForm.get('language')?.value[0].id,
      original: this.downloadForm.get('original')?.value[0].id,
      short: this.downloadForm.get('short')?.value,
      anonymous: this.downloadForm.get('anonymous')?.value
    };
  }

  private buildLink(): string {
    const params = this.getDownloadParams();
    const path = `${encodeURIComponent(this.email ?? '')}?${new URLSearchParams(params as any)}`;
    return `${environment.profileUrl}/${path}`;
  }

  copyLinkHandler() {
    navigator.clipboard.writeText(this.buildLink()).then(async () => {
      const heading = await firstValueFrom(this.translateService.get('action.copy_link.message'));
      this.toastService.add({ heading, life: 5000 });
    });
  }

  updateHandler() {
    const path = `${this.email}/sync`;
    const url = `${environment.profileUrl}/${path}`;
    firstValueFrom(this.http.get(`${url}`, { responseType: 'text' })).then(
      async (response) => {
        console.log('Update success:', response);
        const heading = await firstValueFrom(this.translateService.get('action.update_profile.message'));
        this.toastService.add({ heading, life: 5000 });
      },
      (e) => {
        return console.log('Update error', e);
      }
    );
  }

  downloadHandler() {
    if (!this.email) return;
    const params = this.getDownloadParams();
    this.profileService.downloadProfile(this.email, params).subscribe({
      next: (blob: Blob) => {
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        const extension = params.format; // 'pdf' or 'docx'
        link.download = `profile_${this.email}.${extension}`;
        link.click();
        window.URL.revokeObjectURL(url);
        this.closeDialog.emit(DialogCloseAction.YES);
      },
      error: (err: any) => {
        console.error('Download failed', err);
      }
    });
  }
}
